"use client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@oreme/ui";
import { CircleUser } from "lucide-react";
import Link from "next/link";
import { useTranslations, useLocale } from "next-intl";
import React, { type ReactElement } from "react";
import { User } from "@/types";
import { GlobalLocaleSwitcher } from "@/components";
import { userNavItems } from "@/services/applications";

interface UserMenuProps {
  user: User | null | undefined;
}

function UserMenu({ user }: Readonly<UserMenuProps>): ReactElement {
  const t = useTranslations("global.UserMenu");
  const locale = useLocale();

  if (user?.email) {
    return (
      <TooltipProvider>
        <Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="outline"
                  aria-label={t("tooltip")}
                  className="rounded-full"
                >
                  <CircleUser strokeWidth={1} />
                </Button>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent>{t("tooltip")}</TooltipContent>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
              <DropdownMenuGroup>
                {userNavItems.map((item) => {
                  return (
                    <DropdownMenuItem key={item.id} asChild>
                      <Link href={item.url}>{item.title[locale]}</Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>{t("settings")}</DropdownMenuLabel>
              <DropdownMenuGroup>
                <GlobalLocaleSwitcher />
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/login/logout" prefetch={false}>
                  {t("logout")}
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                aria-label={t("tooltip")}
                className="rounded-full"
              >
                <CircleUser strokeWidth={1} />
              </Button>
            </TooltipTrigger>
          </DropdownMenuTrigger>
          <TooltipContent>{t("tooltip")}</TooltipContent>
          <DropdownMenuContent align="end" className="w-56">
            <div className="p-0 m-2 focus:text-inherit">
              <Button
                size="sm"
                className="w-full hover:text-primary-foreground!"
                asChild
              >
                <Link href="/login" prefetch={false}>
                  {t("login")}
                </Link>
              </Button>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("settings")}</DropdownMenuLabel>
            <DropdownMenuGroup>
              <GlobalLocaleSwitcher />
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </Tooltip>
    </TooltipProvider>
  );
}

export { UserMenu };
